import { injectable } from 'inversify';

const listenerTimeout = 2000;
const MESSAGE_SOURCE = 'desygner-viewer'; // To signal the iframe parent to only listen to messages from the viewer

export interface IPostMessageManager {
  isEmbedded: () => boolean;
  sendToParent: (message: { type: string; payload: any }) => void;
  listenForParent: (handler: (message: any) => void) => void;
  receiveCredentialsFromParent: () => Promise<{ userToken: string; userHash: string } | null>;
  dispose: () => void;
}

@injectable()
export class PostMessageManager implements IPostMessageManager {
  private messageHandler: ((event: MessageEvent) => void) | null = null;

  public isEmbedded(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  private getTargetOrigin(): string {
    const currentOrigin = window.location.origin;

    if (currentOrigin.includes('localhost')) {
      return '*';
    }

    if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer);

        if (this.isValidOrigin(referrerUrl.origin)) {
          return referrerUrl.origin;
        }
      } catch (error) {
        console.error('[PostMessageManager] Failed to parse referrer:', document.referrer, error);
      }
    }

    return currentOrigin.replace('viewer.', '');
  }

  private isValidOrigin(origin: string): boolean {
    try {
      const url = new URL(origin);

      if (url.hostname === 'localhost' || url.hostname.endsWith('.localhost')) {
        return true;
      }

      const allowedPatterns = [/^.*desygner\.com$/, /^.*desygner\.xyz$/];

      return allowedPatterns.some((pattern) => pattern.test(url.hostname));
    } catch {
      return false;
    }
  }

  public sendToParent(message: { type: string; payload: any }): void {
    if (!this.isEmbedded()) {
      return;
    }

    try {
      const targetOrigin = this.getTargetOrigin();
      const messageWithSource = { ...message, source: MESSAGE_SOURCE };

      window.parent.postMessage(messageWithSource, targetOrigin);
    } catch (error) {
      console.error('[PostMessageManager] Failed to send message to parent:', error);
    }
  }

  public listenForParent(handler: (message: any) => void): void {
    if (!this.isEmbedded()) {
      return;
    }

    this.messageHandler = (event: MessageEvent) => {
      if (!this.isValidOrigin(event.origin)) {
        return;
      }

      try {
        handler(event.data);
      } catch (error) {
        console.error('[PostMessageManager] Error handling parent message:', error);
      }
    };

    window.addEventListener('message', this.messageHandler);
  }

  public receiveCredentialsFromParent(): Promise<{ userToken: string; userHash: string } | null> {
    return new Promise((resolve) => {
      this.dispose();

      const timeout = setTimeout(() => {
        console.log('[PostMessageManager] No credentials received from parent within timeout');
        this.dispose();
        resolve(null);
      }, listenerTimeout);

      this.listenForParent((message) => {
        if (message.type === 'SET_CREDENTIALS' && message.payload) {
          clearTimeout(timeout);
          this.dispose();
          resolve(message.payload);
        }
      });

      this.sendToParent({
        type: 'CREDENTIALS_READY',
        payload: {},
      });
    });
  }

  public dispose(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
  }
}
